const DEFAULT_MAX_CAPTIONS = 50;

function trimToMax(captions, maxCaptions = DEFAULT_MAX_CAPTIONS) {
  const safeMax = Number.isFinite(maxCaptions) && maxCaptions > 0 ? maxCaptions : DEFAULT_MAX_CAPTIONS;
  return captions.slice(-safeMax);
}

export function buildCaptionAdmissionKey(data) {
  if (data?.isTranslation && data?.segmentId) {
    return `segment:${data.segmentId}`;
  }

  const itemId = data?.itemId || "";
  const contentIndex = Number.isInteger(data?.contentIndex) ? data.contentIndex : 0;
  return `${itemId}:${contentIndex}`;
}

function captionKey(caption) {
  return buildCaptionAdmissionKey(caption);
}

function mergeDeltaText(previousText, incomingText) {
  const previous = typeof previousText === "string" ? previousText : "";
  const incoming = typeof incomingText === "string" ? incomingText : "";

  if (!previous) return incoming;
  if (!incoming) return previous;

  // Some providers stream cumulative text while others stream true deltas.
  if (incoming.startsWith(previous)) {
    return incoming;
  }

  return `${previous}${incoming}`;
}

export function downsampleFloat32(input, inputRate, outputRate) {
  if (!(input instanceof Float32Array) || input.length === 0) {
    return new Float32Array(0);
  }

  if (!Number.isFinite(inputRate) || !Number.isFinite(outputRate) || outputRate <= 0) {
    return input;
  }

  if (outputRate >= inputRate) {
    return input;
  }

  const ratio = inputRate / outputRate;
  const newLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < output.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < input.length; i += 1) {
      accum += input[i];
      count += 1;
    }
    output[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }

  return output;
}

export function float32ToInt16(float32Array) {
  const int16 = new Int16Array(float32Array.length);

  for (let i = 0; i < float32Array.length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }

  return int16;
}

function isFloat32Frame(value) {
  return value instanceof Float32Array && value.length > 0;
}

function isInt16Frame(value) {
  return value instanceof Int16Array && value.length > 0;
}

export function extractVadFrame(primaryArg, secondaryArg) {
  if (isFloat32Frame(secondaryArg)) {
    return secondaryArg;
  }

  if (isFloat32Frame(primaryArg)) {
    return primaryArg;
  }

  if (isFloat32Frame(primaryArg?.frame)) {
    return primaryArg.frame;
  }

  if (isFloat32Frame(primaryArg?.audio)) {
    return primaryArg.audio;
  }

  if (isFloat32Frame(secondaryArg?.frame)) {
    return secondaryArg.frame;
  }

  if (isFloat32Frame(secondaryArg?.audio)) {
    return secondaryArg.audio;
  }

  return null;
}

export function extractSpeechEndAudio(payload) {
  if (isFloat32Frame(payload)) {
    return payload;
  }

  if (Array.isArray(payload) && payload.length > 0 && typeof payload[0] === "number") {
    return Float32Array.from(payload);
  }

  if (isFloat32Frame(payload?.audio)) {
    return payload.audio;
  }

  if (Array.isArray(payload?.audio) && payload.audio.length > 0 && typeof payload.audio[0] === "number") {
    return Float32Array.from(payload.audio);
  }

  return null;
}

export function splitInt16IntoChunks(pcm16, samplesPerChunk = 2048) {
  if (!isInt16Frame(pcm16)) {
    return [];
  }

  const safeChunkSize = Number.isInteger(samplesPerChunk) && samplesPerChunk > 0 ? samplesPerChunk : 2048;
  const chunks = [];

  for (let offset = 0; offset < pcm16.length; offset += safeChunkSize) {
    chunks.push(pcm16.slice(offset, offset + safeChunkSize));
  }

  return chunks;
}

export function buildStartTranscriptionPayload({ uuid, roomId, speaker, speakerId, speakerRoomSessionId }) {
  return {
    uuid,
    roomId,
    speaker,
    speakerId,
    speakerRoomSessionId
  };
}

export function shouldConnectSendTranscriptionSession({ entered, hasTranscriptionAgent, roomId }) {
  return Boolean(entered && hasTranscriptionAgent && roomId);
}

export function rememberCaptionAdmission(cache, key, decision) {
  if (cache.has(key)) {
    return cache.get(key);
  }

  const normalizedDecision = Boolean(decision);
  cache.set(key, normalizedDecision);
  return normalizedDecision;
}

export function dropCaptionAdmission(cache, data) {
  cache.delete(buildCaptionAdmissionKey(data));
}

export function buildReceiveSubscriptionPayload({ roomId, translationEnabled, targetLanguage }) {
  if (translationEnabled && targetLanguage) {
    return {
      roomId,
      mode: "translation",
      targetLanguage
    };
  }

  return {
    roomId,
    mode: "original"
  };
}

export function applyTranscriptDelta(captions, data, maxCaptions = DEFAULT_MAX_CAPTIONS) {
  if (!data || !data.itemId) return captions;

  const key = captionKey(data);
  const existingIndex = captions.findIndex(caption => !caption.isFinal && captionKey(caption) === key);

  if (existingIndex === -1) {
    return trimToMax([...captions, { ...data, isFinal: false, timestamp: Date.now() }], maxCaptions);
  }

  const existing = captions[existingIndex];
  const updated = {
    ...existing,
    ...data,
    text: mergeDeltaText(existing.text, data.text),
    isFinal: false,
    timestamp: Date.now()
  };

  const next = [...captions];
  next[existingIndex] = updated;
  return trimToMax(next, maxCaptions);
}

export function applyTranscriptDone(captions, data, maxCaptions = DEFAULT_MAX_CAPTIONS) {
  if (!data || !data.itemId) return captions;

  const key = captionKey(data);
  const filtered = captions.filter(caption => !(captionKey(caption) === key && !caption.isFinal));

  return trimToMax([...filtered, { ...data, isFinal: true, timestamp: Date.now() }], maxCaptions);
}

export function appendTranslation(captions, data, maxCaptions = DEFAULT_MAX_CAPTIONS) {
  if (!data || !data.text) return captions;

  return trimToMax(
    [
      ...captions,
      {
        segmentId: data.segmentId,
        speaker: data.speaker,
        speakerId: data.speakerId,
        speakerRoomSessionId: data.speakerRoomSessionId,
        text: data.text,
        sourceText: data.sourceText,
        targetLanguage: data.targetLanguage,
        isTranslation: true,
        isFinal: true,
        timestamp: Date.now()
      }
    ],
    maxCaptions
  );
}

export function pruneExpiredCaptions(captions, maxAgeMs, now = Date.now()) {
  if (!Array.isArray(captions) || captions.length === 0) {
    return captions;
  }

  const safeMaxAge = Number.isFinite(maxAgeMs) && maxAgeMs >= 0 ? maxAgeMs : 0;
  const safeNow = Number.isFinite(now) ? now : Date.now();
  const filtered = captions.filter(caption => {
    if (!Number.isFinite(caption?.timestamp)) {
      return true;
    }

    return safeNow - caption.timestamp <= safeMaxAge;
  });

  return filtered.length === captions.length ? captions : filtered;
}

export function pruneDynamicallyInaudibleCaptions(captions, shouldRenderCaption, { visibleLineCount = 3 } = {}) {
  if (!Array.isArray(captions) || captions.length === 0 || typeof shouldRenderCaption !== "function") {
    return captions;
  }

  const safeVisibleLineCount = Number.isInteger(visibleLineCount) && visibleLineCount > 0 ? visibleLineCount : 3;
  const candidateIndices = new Set();
  const visibleStartIndex = Math.max(0, captions.length - safeVisibleLineCount);

  for (let index = visibleStartIndex; index < captions.length; index += 1) {
    candidateIndices.add(index);
  }

  captions.forEach((caption, index) => {
    if (caption?.isFinal === false) {
      candidateIndices.add(index);
    }
  });

  if (candidateIndices.size === 0) {
    return captions;
  }

  const renderDecisionBySpeaker = new Map();
  const filtered = [];
  let changed = false;

  captions.forEach((caption, index) => {
    if (!candidateIndices.has(index)) {
      filtered.push(caption);
      return;
    }

    const speakerKey = [caption?.speakerRoomSessionId || "", caption?.speakerId || "", caption?.speaker || ""].join(
      ":"
    );
    let shouldRender = renderDecisionBySpeaker.get(speakerKey);

    if (typeof shouldRender === "undefined") {
      shouldRender = Boolean(shouldRenderCaption(caption));
      renderDecisionBySpeaker.set(speakerKey, shouldRender);
    }

    if (shouldRender) {
      filtered.push(caption);
    } else {
      changed = true;
    }
  });

  return changed ? filtered : captions;
}
